import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftRight, ChevronRight, LogOut, ShieldCheck } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api, errorText, SIGN_IN } from './api.ts'
import { Button } from './components/Button.tsx'
import { DropdownItem } from './components/Dropdown.tsx'
import { LogoMark } from './components/Logo.tsx'
import { UserProfileDropdown } from './components/UserProfileDropdown.tsx'
import { Companies } from './pages/admin/Companies.tsx'
import { CompanyView } from './pages/admin/CompanyView.tsx'
import { HostApps } from './pages/admin/HostApps.tsx'
import { Home } from './pages/Home.tsx'
import { Jobs } from './pages/Jobs.tsx'
import { Settings } from './pages/Settings.tsx'
import type { Me } from './types.ts'
import { AdminTag, CompanyMark, Loading, Notice } from './ui.tsx'

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/me') })
}

export function App() {
  const me = useMe()
  if (me.isPending) return <Centered><Loading /></Centered>
  if (me.isError) return <Centered><Notice>{errorText(me.error)}</Notice></Centered>
  const data = me.data

  if (!data.chosen) {
    if (data.refusal && !data.choices.length) return <Refusal message={data.refusal} />
    return <Chooser me={data} />
  }
  return <Shell me={data} />
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="centered">
      <div className="card narrow">{children}</div>
    </div>
  )
}

function Refusal({ message }: { message: string }) {
  return (
    <Centered>
      <LogoMark size={56} />
      <h1>Can't sign you in</h1>
      <p className="muted">{message}</p>
      <Button size="large" fullWidth href={`${SIGN_IN}/microsoft?prompt=select_account`}>
        Use a different account
      </Button>
      <Button variant="secondary" size="large" fullWidth href="/signout">
        Sign out
      </Button>
    </Centered>
  )
}

/** Someone in several companies chooses one first (PLAN.md 1.6). Someone in one goes
 *  straight in, and an Icefield administrator in none goes straight to administration.
 *  Administration is reached from the account menu, so it isn't a choice here. */
function Chooser({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const choose = useMutation({
    mutationFn: (companyId: string | null) => api.post('/session', { companyId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries()
      navigate('/')
    },
  })
  const only =
    me.choices.length === 1 ? me.choices[0]!.companyId : me.choices.length === 0 && me.isIcefieldAdmin ? null : undefined
  // Once only: development builds run effects twice.
  const started = useRef(false)
  useEffect(() => {
    if (only !== undefined && !started.current) {
      started.current = true
      choose.mutate(only)
    }
  }, [only, choose])
  if (only !== undefined) return <Centered>{choose.isError ? <Notice>{errorText(choose.error)}</Notice> : <Loading />}</Centered>

  return (
    <Centered>
      <h1>Choose a company</h1>
      {me.choices.length > 0 ? (
        <p className="muted">You have an account with {me.choices.length === 1 ? 'one company' : 'more than one'}.</p>
      ) : (
        <p className="muted">You're not a member of any company.</p>
      )}
      <Notice>{choose.isError && errorText(choose.error)}</Notice>
      {me.choices.map((c) => (
        <button key={c.companyId} type="button" className="choice" disabled={choose.isPending} onClick={() => choose.mutate(c.companyId)}>
          <CompanyMark name={c.name} logo={c.logo} size={36} />
          <span>{c.name}</span>
          <ChevronRight className="chevron" aria-hidden="true" />
        </button>
      ))}
      <Button variant="destructive-tertiary" fullWidth href="/signout">
        Sign out
      </Button>
    </Centered>
  )
}

function Shell({ me }: { me: Me }) {
  const navigate = useNavigate()
  const current = me.current
  const isCompanyAdmin = current?.person.iceRole === 'company_admin'
  const canSwitch = me.choices.length > 1
  const home = current ? '/' : '/admin'
  // Every Icefield administration page lives under /admin, and wears a blue line across the
  // top of the bar so it's never mistaken for the company's own dashboard.
  const inAdmin = useLocation().pathname.startsWith('/admin')

  const queryClient = useQueryClient()
  const name = current?.person.displayName ?? me.identity.name ?? me.identity.email ?? 'Account'
  // Leaving administration goes back to the company dashboard, or to choosing a company for
  // someone who came in with none chosen. An administrator in no company has nowhere to go.
  const exitAdmin = () => {
    if (current) navigate('/')
    else queryClient.setQueryData<Me>(['me'], (m) => (m ? { ...m, chosen: false, current: null } : m))
  }

  return (
    <div className="shell">
      {inAdmin ? (
        <header className="topbar topbar-admin">
          <div className="admin-who">
            <span className="muted">Logged in as</span> <strong>{name}</strong>
            <AdminTag />
          </div>
          <nav className="nav" aria-label="Administration">
            <NavLink to="/admin/companies">Companies</NavLink>
            <NavLink to="/admin/apps">Apps</NavLink>
          </nav>
          {me.choices.length > 0 && (
            <Button variant="tertiary" icon={LogOut} className="admin-exit" onClick={exitAdmin}>
              Exit Admin Panel
            </Button>
          )}
        </header>
      ) : (
      <header className="topbar">
        <Link to={home} className="brand">
          {current ? (
            <>
              <CompanyMark name={current.company.name} logo={current.company.logoUrl} size={32} />
              <span>{current.company.name}</span>
            </>
          ) : (
            <>
              <LogoMark size={28} />
              <span>Icefield administration</span>
            </>
          )}
        </Link>
        <nav className="nav" aria-label="Main">
          {current && <NavLink to="/" end>Apps</NavLink>}
          {isCompanyAdmin && <NavLink to="/jobs">Jobs</NavLink>}
        </nav>
        <UserProfileDropdown
          name={name}
          email={me.identity.email}
          signOutHref="/signout"
          first={
            me.isIcefieldAdmin && (
              <DropdownItem icon={ShieldCheck} accent onClick={() => navigate('/admin')}>
                Administrator
              </DropdownItem>
            )
          }
          onSettings={() => navigate('/settings')}
        >
          {canSwitch && <SwitchCompany />}
        </UserProfileDropdown>
      </header>
      )}
      <main className="content">
        <Routes>
          <Route path="/" element={current ? <Home me={me} /> : <Navigate to="/admin" replace />} />
          {/* An MCP server's page moved under Settings; links to the old address still work. */}
          {current && <Route path="/mcp-servers/:appId" element={<OldMcpServerAddress />} />}
          <Route path="/settings/*" element={<Settings me={me} />} />
          {/* Where these used to live. */}
          {isCompanyAdmin && <Route path="/users" element={<Navigate to="/settings/users" replace />} />}
          {isCompanyAdmin && <Route path="/people" element={<Navigate to="/settings/users" replace />} />}
          {isCompanyAdmin && <Route path="/licenses" element={<Navigate to="/settings/licenses" replace />} />}
          {isCompanyAdmin && <Route path="/company" element={<Navigate to="/settings/company" replace />} />}
          {isCompanyAdmin && current && <Route path="/jobs" element={<Jobs companyId={current.company.id} />} />}
          {me.isIcefieldAdmin && <Route path="/admin" element={<Navigate to="/admin/companies" replace />} />}
          {me.isIcefieldAdmin && <Route path="/admin/companies" element={<Companies />} />}
          {me.isIcefieldAdmin && <Route path="/admin/apps" element={<HostApps />} />}
          {me.isIcefieldAdmin && <Route path="/admin/companies/:companyId/*" element={<CompanyView />} />}
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </main>
    </div>
  )
}

/** Forget the current choice, so the chooser shows again. */
function SwitchCompany() {
  const queryClient = useQueryClient()
  return (
    <DropdownItem
      icon={ArrowLeftRight}
      onClick={() => {
        queryClient.setQueryData<Me>(['me'], (me) => (me ? { ...me, chosen: false, current: null } : me))
      }}
    >
      Switch company
    </DropdownItem>
  )
}

/** /mcp-servers/<id>, as given out before MCP servers moved under Settings. */
function OldMcpServerAddress() {
  return <Navigate to={`/settings/mcp-servers/${useParams().appId}`} replace />
}
